import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	OnChanges,
	Output,
	SimpleChanges,
	ViewChild,
} from '@angular/core';
import { FormControlStatus, NgForm } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { ICON } from '@core/util/icon';
import {
	CloserDefaultRequisitionPrecedent,
	CustomRequisitionPrecedent,
	RequisitionPrecedent,
} from '@models/requisitions';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { SnackbarService } from '@services/ui/snackbar.service';
import { BusinessRequisitionsState } from '@state/business/requisition/business_requisitions.state';
import { Observable } from 'rxjs';
import { DropdownResult } from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { NewCustomRequisitionModalComponent } from '../../modals/custom-requisition/new-custom-requisition-modal/new-custom-requisition-modal.component';
import { NewRequisitionModalComponent } from '../../modals/matter-requisition/new-requisition-modal/new-requisition-modal.component';
import { take } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
	type Timestamp,
	serverTimestamp as serverTimestampFireStore,
} from 'firebase/firestore';
import { isCloserDefaultRequisition } from '@globalUtils/requisition/requisition';
import { BusinessUsersState } from '@state/business-users/business-users.state';
import { AuthState } from '@state/auth';
import { TestHooks } from './custom-requisition-form.hooks';

// Cast the serverTimestamp to a type that matches the expected Timestamp type from firestore
// This is a workaround for the type mismatch between TimeStamp and FieldValue, returned by serverTimestamp.
const serverTimestamp = () =>
	serverTimestampFireStore() as unknown as Timestamp;

@UntilDestroy()
@Component({
	selector: 'app-custom-requisition-form',
	templateUrl: './custom-requisition-form.component.html',
	styleUrls: ['./custom-requisition-form.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class CustomRequisitionFormComponent
	implements OnChanges, AfterViewInit
{
	testHooks = TestHooks;
	@ViewChild('form') form: NgForm;
	@Select(BusinessRequisitionsState.getAll) requisitions$: Observable<
		RequisitionPrecedent[]
	>;
	@SelectSnapshot(AuthState.getUserId) userId: string;
	@SelectSnapshot(BusinessUsersState.getLoggedInUserFullName)
	public loggedInUserFullName: string;
	@Input() selectedRequisitionId: string;
	@Input() public secondaryButtonText = 'Cancel';
	@Input() public isEdit = false;
	@Output() public onSubmit = new EventEmitter<CustomRequisitionPrecedent>();
	@Output() public formStatusChange = new EventEmitter<FormControlStatus>();

	public requisitionPrecedent = { isOnTitle: true } as RequisitionPrecedent;
	public requisitionTypeItems = [
		{ value: true, label: 'On Title' },
		{ value: false, label: 'Off Title' },
	];

	public initialRequisitionTexts = { text: '', requisitionText: '' };
	public selectedDefaultRequisitionName = '';
	public X_MARK_ICON = ICON.XMARK;

	constructor(
		private dialogRef: MatDialogRef<
			NewCustomRequisitionModalComponent | NewRequisitionModalComponent,
			CustomRequisitionPrecedent
		>,
		private snackbarService: SnackbarService,
	) {}

	ngOnChanges(changes: SimpleChanges): void {
		if (
			changes.selectedRequisitionId.currentValue ===
			changes.selectedRequisitionId.previousValue
		)
			return;
		this.requisitions$.pipe(take(1)).subscribe((requisitions) => {
			this.onRequisitionSelect({
				result: requisitions.find(
					(requisition) =>
						requisition.id === changes.selectedRequisitionId.currentValue,
				),
			});
		});
	}

	ngAfterViewInit(): void {
		this.form.statusChanges
			.pipe(untilDestroyed(this))
			.subscribe((status: FormControlStatus) => {
				this.formStatusChange.emit(status);
			});
	}

	onRequisitionSelect(
		selection: Pick<DropdownResult<RequisitionPrecedent>, 'result'>,
	) {
		if (!selection.result) return;
		this.selectedDefaultRequisitionName = selection.result.name;
		this.requisitionPrecedent = {
			...selection.result,
			name:
				this.isEdit && !this.requisitionPrecedent.name
					? this.selectedDefaultRequisitionName
					: this.requisitionPrecedent.name,
		};
		this.initialRequisitionTexts = {
			text: this.requisitionPrecedent.text,
			requisitionText: this.requisitionPrecedent.requisitionText,
		};
	}

	closeModal() {
		this.dialogRef.close();
	}

	async _onSubmit(form: NgForm) {
		if (form.valid) {
			return this.onSubmit.emit({
				...this.requisitionPrecedent,
				id: null,
				lastEditedBy: this.loggedInUserFullName,
				lastEditedById: this.userId,
				lastEditedOn: serverTimestamp(),
				closerDefault: false,
				defaultRequisitionId: isCloserDefaultRequisition(
					this.requisitionPrecedent,
				)
					? this.requisitionPrecedent.id
					: this.requisitionPrecedent.defaultRequisitionId,
			});
		}

		this.snackbarService.openSnackBar({
			text: 'Error submitting custom requisition request. If the error persists, please contact customer support',
			type: 'error',
			icon: ICON.CIRCLE_X,
		});
	}

	onRequisitionNameChange(name: string) {
		this.onChange('name', name);
	}

	onRequisitionTypeChange(isOnTitle: boolean) {
		this.onChange('isOnTitle', isOnTitle);
	}

	onInstrumentTextChange(updatedText: string) {
		this.onChange('text', updatedText);
	}

	onRequisitionTextChange(updatedText: string) {
		this.onChange('requisitionText', updatedText);
	}

	private onChange = <K extends keyof CloserDefaultRequisitionPrecedent>(
		key: K,
		value: CloserDefaultRequisitionPrecedent[K],
	) => {
		this.requisitionPrecedent = { ...this.requisitionPrecedent, [key]: value };
	};
}
